import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import TiptapEditor from '../../tiptapEditor';
import { CommentFilesInterface } from '../../../interfaces/TaskInterface';
import { CommentInputSection, UploadDiv } from './styles';
import { EmojiClickData } from 'emoji-picker-react';
import { buildCommentUploadFolder, isMediaRecordingSupported } from '../../../helpers/common';
import Button from '../../button';
import { UserInterface } from '../../../interfaces/chatMessageInterface';
import { ReplyMessageDataInterface } from '../../../interfaces/MessageInterface';
import Modal from '../../modals/modal';
import InsertGifModal from '../../modals/insertGifModal';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { observer } from 'mobx-react-lite';
import { rootStore } from '../../../mobx/rootStore';
import { BUTTON_TYPE } from '../../../global/constants';
import { useMobileDevice } from '../../../global/useMobile';
import { TiptapEditorRef } from '../../../interfaces/tiptapInterface';
import { useCommentDraft } from './hooks/useCommentDraft';
import { useCommentFiles } from './hooks/useCommentFiles';
import { CommentToolbar } from './CommentToolbar';
import { CommentFilePreview } from './CommentFilePreview';

interface BaseProps {
  disable?: boolean;
  onClickSelectFile?: () => void;
  users?: UserInterface[];
  openModal?: () => void;
  replyMessage?: ReplyMessageDataInterface;
  setReplyMessage?: (value: ReplyMessageDataInterface) => void;
  propsLoading?: boolean;
  setPropsLoading?: (value: boolean) => void;
  handleOpenSnapModal?: () => void;
  /** External file list — used by both modes for out-of-band file selection (e.g. existing project files). */
  uploadedFiles?: CommentFilesInterface[];
  setUploadedFiles?: (
    files: CommentFilesInterface[] | ((prevFiles: CommentFilesInterface[]) => CommentFilesInterface[])
  ) => void;
  /** Project the task belongs to. Used (with taskId) to store comment files under
   *  `projects/{projectId}/tasks/{taskId}/comments/`. Falls back to `files/` when absent. */
  projectId?: string;
}

/** Managed mode: CommentInputBox owns all comment text state; parent supplies taskId + onSend. */
interface ManagedProps extends BaseProps {
  taskId: string;
  onSend: (
    files: CommentFilesInterface[],
    comment: string,
    isEdit?: boolean
  ) => Promise<boolean> | Promise<void> | void;
  valueContent?: never;
  setValueContent?: never;
  sendComment?: never;
}

/** Legacy mode: parent manages comment text state externally. */
interface LegacyProps extends BaseProps {
  taskId?: never;
  onSend?: never;
  valueContent?: string;
  setValueContent?: (value: string) => void;
  sendComment?: (files: CommentFilesInterface[]) => void;
}

type Props = ManagedProps | LegacyProps;

const CommentInputBoxComponent = forwardRef((props: Props, ref) => {
  const {
    valueContent: externalValueContent,
    setUploadedFiles: externalSetUploadedFiles,
    sendComment: externalSendComment,
    uploadedFiles: externalUploadedFiles,
    disable = false,
    onClickSelectFile,
    users = [],
    replyMessage = {},
    setReplyMessage = () => {},
    propsLoading = false,
    setPropsLoading = () => {},
    taskId,
    projectId,
    onSend,
    handleOpenSnapModal = () => {}
  } = props || {};
  // Comment files (images + attachments) are stored under the task's own folder when we know
  // both ids; otherwise fall back to the generic `files/` folder so nothing breaks.
  const uploadFolder = buildCommentUploadFolder(projectId, taskId);
  // Refs
  const editorRef = useRef<TiptapEditorRef>(null);
  // Local state
  // Note: externalValueContent initializes state in legacy mode. In managed mode,
  // it is never provided (via ManagedProps), so state safely defaults to '' until the draft loads.
  const [valueContent, setValueContent] = useState<string>(externalValueContent || '');
  const [buttonDisable, setButtonDisable] = useState<boolean>(false);
  const [openGifModal, setIsOpenGifModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const setDragCounter = useState(0)[1];
  // Mobx store variables
  const { projectStore } = rootStore;
  const { uploadFilePercent } = projectStore;
  // Custom hooks for file and draft management
  const {
    uploadedFiles,
    setUploadedFilesLocal,
    loading,
    setLoading,
    fileInputRef,
    onFileUpload,
    onClickClose,
    handleFileInput,
    addDroppedFiles
  } = useCommentFiles(externalUploadedFiles, externalSetUploadedFiles, editorRef, taskId, uploadFolder);
  const { clearDraft } = useCommentDraft(taskId, valueContent, uploadedFiles, setValueContent, setUploadedFilesLocal);
  // Other variables
  const isMobile = useMobileDevice();

  // Sync external comment text to local state (legacy mode only).
  // In managed mode (taskId provided) CommentInputBox owns its state — skip this sync to
  // prevent the external prop from racing against the draft loaded in the effect below.
  useEffect(() => {
    if (taskId) return;
    if (externalValueContent !== undefined) {
      setValueContent(externalValueContent);
    }
  }, [externalValueContent, taskId]);

  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Only show DropBox for external file drags
      const hasFiles = e.dataTransfer?.types.includes('Files');
      if (!hasFiles) return;

      setDragCounter((prev) => {
        const newVal = prev + 1;
        if (newVal > 0) setIsDragging(true);
        return newVal;
      });
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Only handle file drags
      const hasFiles = e.dataTransfer?.types.includes('Files');
      if (!hasFiles) return;

      setDragCounter((prev) => {
        const newVal = prev - 1;
        if (newVal <= 0) {
          setIsDragging(false);
          return 0;
        }
        return newVal;
      });
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Only handle file drags
      const hasFiles = e.dataTransfer?.types.includes('Files');
      if (!hasFiles) return;

      setIsDragging(false);
      setDragCounter(0);
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);
    window.addEventListener('dragend', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
      window.removeEventListener('dragend', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
    };
  }, [setDragCounter]);

  // Ref functions which are call from parent component
  useImperativeHandle(ref, () => ({
    addRecordingCard(fileUrl: string, fileName: string) {
      editorRef.current?.addRecordingCard(fileUrl, fileName);
    },
    addContent(text: string) {
      editorRef.current?.addContent(text);
    },
    addImageInContent(fileUrl: string) {
      editorRef.current?.addImage(fileUrl);
    },
    addImageFileInContent(file: File | string) {
      editorRef.current?.addImageFile([file as File]);
    },
    clearContent() {
      editorRef?.current?.clearValue();
    }
  }));

  // Function call when upload snap recording card
  const uploadSnapEmbed = useCallback((firebaseUrl: string, fileName: string, videoId: string) => {
    editorRef?.current?.addRecordingCard(firebaseUrl, fileName, videoId);
  }, []);

  // On select emoji
  const handleEmojiSelected = useCallback((emojiData: EmojiClickData) => {
    const currentEmoji = UserPreferenceSingleton.getInstance().getUserUseEmoji();
    const updatedEmojis = [
      emojiData?.emoji,
      ...(Array.isArray(currentEmoji) ? currentEmoji.filter((e) => e !== emojiData?.emoji) : [])
    ]?.slice(0, 2);
    UserPreferenceSingleton.getInstance().setUserUseEmoji(updatedEmojis);
    editorRef.current?.addContentExistingLine(emojiData.emoji);
  }, []);

  const onSendComment = useCallback(() => {
    // If using wrapper mode (taskId + onSend provided), call with draft clearing
    if (taskId && onSend) {
      const result = onSend(uploadedFiles, valueContent, false);
      // Handle both async and sync returns
      if (result && typeof result.then === 'function') {
        result
          .then(() => {
            // Clear drafts on success
            setValueContent('');
            setUploadedFilesLocal([]);
            clearDraft();
          })
          .catch(() => {
            // Leave draft intact on error
          });
      } else {
        // Sync return or Promise<void>
        setValueContent('');
        setUploadedFilesLocal([]);
        clearDraft();
      }
    } else if (externalSendComment) {
      // Legacy mode: use external props
      setUploadedFilesLocal([]);
      if (externalSetUploadedFiles) {
        externalSetUploadedFiles([]);
      }
      externalSendComment(uploadedFiles);
    }
  }, [
    taskId,
    onSend,
    uploadedFiles,
    valueContent,
    externalSendComment,
    externalSetUploadedFiles,
    clearDraft,
    setUploadedFilesLocal
  ]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault(); // Prevent default behavior if needed
      if (loading || propsLoading || buttonDisable) return;
      onSendComment();
    }
  };

  const handleOpenGifModal = useCallback(() => {
    setIsOpenGifModal(true);
  }, []);

  const handleCloseGifModal = useCallback(() => {
    setIsOpenGifModal(false);
  }, []);

  // Renders the send button
  const renderSendButton = useCallback(() => {
    return (
      <Button
        title='Send'
        variant={BUTTON_TYPE.PRIMARY}
        onClick={onSendComment}
        disabled={loading || propsLoading || buttonDisable}
        size={'small'}
      />
    );
  }, [buttonDisable, loading, onSendComment, propsLoading]);

  const recordingProp = useMemo(() => {
    return isMediaRecordingSupported()
      ? {
          valueContent,
          setValueContent,
          setLoading,
          setButtonDisable,
          onOpenSnapModal: handleOpenSnapModal,
          uploadSnapEmbed
        }
      : undefined;
  }, [valueContent, setValueContent, setLoading, setButtonDisable, handleOpenSnapModal, uploadSnapEmbed]);

  return (
    <>
      <CommentInputSection onKeyDown={handleKeyDown} isApplyManualHeight={!disable && isDragging}>
        <TiptapEditor
          ref={editorRef}
          valueContent={disable ? '' : valueContent}
          setValueContent={setValueContent}
          placeholder='Leave a comment...'
          users={users}
          disabled={disable}
          upload={{
            setLoading: (value: boolean) => {
              if (setPropsLoading) setPropsLoading(value);
              else setLoading(value);
            },
            setStateLoading: (value: boolean) => setLoading(value),
            addUploadedFiles: addDroppedFiles,
            uploadFolder
          }}
          reply={{
            replyMessage: disable ? undefined : replyMessage,
            setReplyMessage
          }}
          toolbarActions={{
            recordSnapModalOpen: handleOpenSnapModal,
            handleFileInput,
            handleGifModal: handleOpenGifModal
          }}
          dropBox={{ isVisible: true }}
        />

        <input type='file' ref={fileInputRef} hidden onChange={onFileUpload} multiple aria-label='Upload file' />
        {/* file preview */}
        <CommentFilePreview uploadedFiles={uploadedFiles} onClickClose={onClickClose} disable={disable} />
        {/* upload progress */}
        {loading && <UploadDiv>Uploading ({Math.floor(uploadFilePercent)}%)</UploadDiv>}
        {/* toolbar */}
        <CommentToolbar
          handleFileInput={handleFileInput}
          onClickSelectFile={onClickSelectFile}
          onEmojiSelect={handleEmojiSelected}
          isMobile={isMobile}
          renderSendButton={renderSendButton}
          recording={recordingProp}
        />
      </CommentInputSection>
      {/* Modals */}
      <Modal open={openGifModal} onClose={handleCloseGifModal} maxWidth={'686px'} isResponsiveMaxWidth={true}>
        <InsertGifModal
          setSelectedGif={(url: string) => {
            editorRef.current?.addImage(url);
          }}
          closeModal={handleCloseGifModal}
        />
      </Modal>
    </>
  );
});

const CommentInputBox = observer(CommentInputBoxComponent);

export default CommentInputBox;
